import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CreatePayment } from "../utils/PiIntegration";
import { actions } from "../redux";
import emitter, { EmitterEvents } from "../utils/eventsEmitter";
import { THEME_COLORS } from "../themes/DarkLight";
import useThemeHook from "../hooks/useThemeHook";
import { CheckmarkDoneCircleOutline } from "react-ionicons";
import { Zap, Crown, Flame, Sparkles, X } from "lucide-react";

interface DepositPromoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DepositPromoModal: React.FC<DepositPromoModalProps> = ({ isOpen, onClose }) => {
  const [isDepositing, setIsDepositing] = useState(false);
  const [activeAmount, setActiveAmount] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const theme = useThemeHook();
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  if (!isOpen) return null;

  const handleDeposit = async (amount: number) => {
    if (!user?.id) {
      setErrorMsg("User session not found. Please log in again.");
      return;
    }
    setIsDepositing(true);
    setActiveAmount(amount);
    setErrorMsg(null);

    try {
      await CreatePayment(
        user.id,
        amount,
        "deposit",
        `Arthereum Wallet - Deposit ${amount} Pi Boost`,
        async () => {
          // Success Callback
          dispatch(
            actions.setActualUser({
              ...userData,
              user: {
                ...userData.user,
                picoinBalance: (userData.user.picoinBalance || 0) + amount,
              },
            })
          );

          emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
            modal: {
              name: "alertModal",
              modalData: {
                description: `Successfully deposited ${amount} Pi! Your bonus is being credited.`,
                title: "Deposit Successful!",
                icon: (
                  <CheckmarkDoneCircleOutline
                    color="#f59e0b"
                    height="50px"
                    width="50px"
                  />
                ),
              },
            },
          });
          onClose();
        }
      );
    } catch (err: any) {
      console.error(err);
      setErrorMsg(
        err?.message || "Failed to process deposit. Ensure you are using the Pi Browser."
      );
    } finally {
      setIsDepositing(false);
      setActiveAmount(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 rounded-3xl p-6 w-full max-w-sm border border-amber-500/30 shadow-2xl relative overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Glow Effects */}
        <div className="absolute -top-12 -left-12 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute -bottom-12 -right-12 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isDepositing}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mt-2 mb-4">
          <div className="inline-flex items-center justify-center bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1 mb-2 animate-pulse">
            <Sparkles className="w-4 h-4 text-amber-400 mr-1.5" />
            <span className="text-[10px] font-black text-amber-400 uppercase tracking-widest">
              Limited Time Boost
            </span>
          </div>
          <h3 className="text-xl font-black text-white tracking-wide">
            DEPOSIT & MULTIPLY 🚀
          </h3>
          <p className="text-xs text-gray-300 px-3 mt-1 leading-relaxed">
            Instantly multiply your mining speed, claim streak bonuses, and get massive ATH rewards!
          </p>
        </div>

        {/* Error Messaging */}
        {errorMsg && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-2.5 mb-4 text-[10px] text-red-300 text-center font-medium leading-normal">
            {errorMsg}
          </div>
        )}

        {/* Offers Container */}
        <div className="space-y-3">
          
          {/* 100 Pi Offer */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-3 hover:border-amber-500/20 transition-all flex flex-col justify-between items-stretch">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Flame className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-black text-white">Starter Boost</span>
              </div>
              <span className="text-[10px] font-extrabold bg-white/10 text-white px-2 py-0.5 rounded-full">
                100 Pi
              </span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <div className="text-[11px] text-gray-300">
                ⭐ <strong className="text-amber-400 font-black">+500 ATH</strong> bonus
              </div>
              <button
                disabled={isDepositing}
                onClick={() => handleDeposit(100)}
                className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-slate-900 text-[11px] font-black py-1.5 px-3 rounded-xl transition-all shadow-md active:scale-95"
              >
                {isDepositing && activeAmount === 100 ? "Processing..." : "Deposit 100π"}
              </button>
            </div>
          </div>

          {/* 500 Pi Offer */}
          <div className="bg-gradient-to-r from-amber-500/5 to-purple-500/5 border border-amber-500/30 rounded-2xl p-3 hover:border-amber-500/50 transition-all flex flex-col justify-between items-stretch relative">
            <div className="absolute -top-2 right-4 bg-amber-500 text-slate-900 text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider shadow">
              🔥 Most Popular
            </div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-black text-white">Mega Multiplier</span>
              </div>
              <span className="text-[10px] font-extrabold bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full border border-amber-500/20">
                500 Pi
              </span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <div className="text-[11px] text-gray-300">
                ⭐ <strong className="text-amber-400 font-black">+3,000 ATH</strong> + 20x Flash Boost
              </div>
              <button
                disabled={isDepositing}
                onClick={() => handleDeposit(500)}
                className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 disabled:from-amber-500/50 disabled:to-amber-500/50 text-slate-900 text-[11px] font-black py-1.5 px-3 rounded-xl transition-all shadow-lg shadow-amber-500/10 active:scale-95 animate-pulse"
              >
                {isDepositing && activeAmount === 500 ? "Processing..." : "Deposit 500π"}
              </button>
            </div>
          </div>

          {/* 1000 Pi Offer */}
          <div className="bg-white/5 border border-purple-500/20 rounded-2xl p-3 hover:border-purple-500/40 transition-all flex flex-col justify-between items-stretch relative">
            <div className="absolute -top-2 right-4 bg-purple-600 text-white text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider shadow">
              👑 Best Value
            </div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Crown className="w-4 h-4 text-purple-400" />
                <span className="text-xs font-black text-white">Legendary Whale</span>
              </div>
              <span className="text-[10px] font-extrabold bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full border border-purple-500/20">
                1000 Pi
              </span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <div className="text-[11px] text-gray-300">
                ⭐ <strong className="text-purple-300 font-black">+10,000 ATH</strong> + Turbo + 5 Spins
              </div>
              <button
                disabled={isDepositing}
                onClick={() => handleDeposit(1000)}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 text-white text-[11px] font-black py-1.5 px-3 rounded-xl transition-all shadow-md active:scale-95"
              >
                {isDepositing && activeAmount === 1000 ? "Processing..." : "Deposit 1000π"}
              </button>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-[9px] text-gray-400">
            Deposits are processed securely via Pi Network. Zero hidden fees.
          </p>
        </div>

      </div>
    </div>
  );
};

export default DepositPromoModal;
